/**
 * Dorm layout optimizer v2 — constants.
 *
 * All physical dimensions stored in inches; convert to meters at runtime
 * via IN_TO_M or the helper functions at the bottom.
 */

const IN_TO_M = 0.0254;
const WALL_HALF_THICKNESS = 0.075;

/**
 * Default size of a user-marked built-in (closet/cabinet), in inches. Both
 * wizards drop a new built-in at exactly this size — web `DEFAULT_BUILTIN_INCHES`
 * (DormWizardModal.jsx) and iOS `defaultBuiltinInches`
 * (DormBuiltinGeometry.swift). Kept here so the engine can name the same
 * default rather than inventing one when a persisted built-in arrives without a
 * height.
 */
const DEFAULT_BUILTIN_INCHES = { w: 30, d: 24, h: 84 };

/**
 * Candidate layouts kept per bed config before validation and scoring. A big
 * room with a full item list produces ~760 per config; validating and scoring
 * them is most of the search, and the picker shows four per arrangement. 200
 * was measured as the knee: the search 2.3x faster with the top layout's
 * score unchanged (#3111). Sampled evenly, see sampleEvenly in
 * placement/generate.js.
 */
const MAX_CANDIDATES_PER_BED_CONFIG = 200;

/**
 * Walking clearance reserved in front of a built-in (door swing + approach).
 * Mirrored by web `BUILTIN_CLEARANCE_M` and iOS `clearanceMeters`.
 */
const FIXED_OBSTACLE_CLEARANCE_M = 18 * IN_TO_M;

/**
 * ITEM_DIMS_INCHES — canonical per-key footprint, keyed by the persisted iOS
 * key (see dorm-layout-generation.js). Dims are `{w, d, h}` = iOS
 * `(width, depth, height)` (iOS stores width,height,depth — swap the last two).
 *
 * CHECKLIST — adding a new item:
 *   New item → add here + exactly one classify bucket
 *   (DESK_ZONE / SURFACE_HOSTS / WALL_MOUNTED / DOOR_MOUNTED / FREESTANDING /
 *    WALL_ADJACENT) + any behavioral-modifier sets
 *   (NO_CLEARANCE_SMALL / ROTATABLE / LOFT_UNDERBED / CAPTAIN_UNDERBED /
 *    FACING_FLIP / CORNER_CONCEALED / CLEARANCE_ZONES),
 *   or classifyItems (items.js) silently drops it (it has no final `else`).
 */
const ITEM_DIMS_INCHES = {
  // Core dorm-provided items
  'dorm-bed': { w: 42, d: 84, h: 36 },
  'dorm-desk': { w: 42, d: 24, h: 30 },
  'dorm-dresser': { w: 32, d: 24, h: 30 },
  'dorm-wardrobe': { w: 30, d: 24, h: 74 },
  'dorm-bookshelf': { w: 24, d: 12, h: 30 },
  'dorm-desk-chair': { w: 20, d: 20, h: 32 },
  'dorm-wastebasket': { w: 10, d: 8, h: 18 },

  // Personal — wall-adjacent
  'dorm-mini-fridge': { w: 19, d: 20, h: 33 },
  'dorm-shoe-rack': { w: 25, d: 9, h: 23 },
  'dorm-printer': { w: 16, d: 12, h: 6 },
  'dorm-floor-lamp': { w: 10, d: 10, h: 72 },
  'dorm-trash-can': { w: 12, d: 9, h: 20 },
  'dorm-fan': { w: 12, d: 12, h: 36 },
  'dorm-air-purifier': { w: 7, d: 10, h: 27 },

  // Personal — freestanding
  'dorm-laundry-bin': { w: 18, d: 12, h: 24 },
  'dorm-beanbag': { w: 37, d: 37, h: 27 },
  'dorm-3-tier-cart': { w: 14, d: 18, h: 31 },

  // Surface items (placed on host furniture)
  'dorm-microwave': { w: 18, d: 14, h: 11 },
  'dorm-desk-lamp': { w: 7, d: 7, h: 17 },
  'dorm-monitor': { w: 22, d: 8, h: 15 },
  'dorm-tv': { w: 22, d: 7, h: 14 },

  // Wall-mounted (no floor footprint)
  'dorm-command-hooks': { w: 1.72, d: 0.89, h: 4.24 },
  // 20x40, NOT the 14x48 full-length mirror this used to describe. The only
  // dorm-mirror asset is a 20x40" capsule frame that renders at its OWN fixed
  // size (it is not `isScalable`), so a 14x48 declaration reserved a footprint
  // nothing would ever draw into and cut the reflection to the wrong box —
  // #2317. Matches `dorm-styling/plan/constants.js`, so a CHOSEN mirror and a
  // THEMED one are now the same object.
  'dorm-mirror': { w: 20, d: 1, h: 40 },
  'dorm-corkboard': { w: 23, d: 1, h: 17 },
  'dorm-whiteboard': { w: 24, d: 1, h: 18 },
  'dorm-floating-shelf': { w: 24, d: 8, h: 2 },
  'dorm-poster': { w: 24, d: 1, h: 36 },
  'dorm-tapestry': { w: 40, d: 1, h: 60 },

  // Door-mounted (no floor footprint)
  'dorm-over-door-hooks': { w: 20, d: 4, h: 11 },
  'dorm-over-door-mirror': { w: 14, d: 1, h: 48 },

  // De-aliased Group B items (real dims; were stand-in-mapped)
  'dorm-stick-vacuum': { w: 9, d: 8, h: 43 },
  'dorm-handheld-vacuum': { w: 14, d: 5, h: 7 },
  'dorm-floor-plant': { w: 10, d: 10, h: 30 },
  'dorm-desk-plant': { w: 5, d: 5, h: 8 },
  'dorm-fairy-lights': { w: 96, d: 2, h: 8 },
  // 5 ft x 5 ft — a standard dorm LED-curtain panel, and narrow enough to
  // find a free wall zone in a furnished room. At the former 96" it needed a
  // clear 8 ft of wall and went unplaced in 5 of the 27 corpus scenarios.
  'dorm-fairy-lights-curtain': { w: 60, d: 2, h: 60 },
  'dorm-surge-protector': { w: 4.5, d: 3.6, h: 1.6 },
  'dorm-storage-bin': { w: 33, d: 18, h: 7 },
  'dorm-stackable-drawers': { w: 24, d: 18, h: 24 },

  // De-aliased Group C variants (own dims; were collapsed to one footprint)
  'dorm-minifridge-compact': { w: 18, d: 18, h: 20 },
  'dorm-minifridge-midsize': { w: 19, d: 20, h: 33 },
  'dorm-tower-fan': { w: 6, d: 6, h: 30 },
  'dorm-desk-fan': { w: 8, d: 7, h: 11 },
  'dorm-air-purifier-tower': { w: 8, d: 8, h: 22 },
  'dorm-air-purifier-compact': { w: 7, d: 7, h: 12 },
  'dorm-rolling-cart': { w: 18, d: 14, h: 33 },
  'dorm-laundry-hamper': { w: 14, d: 14, h: 24 },
};

// Clearance zones (full front-face width x depth)
const CLEARANCE_ZONES_INCHES = {
  'dorm-desk': { w: 42, d: 24 },
  'dorm-dresser': { w: 32, d: 24 },
  'dorm-wardrobe': { w: 30, d: 24 },
  'dorm-bookshelf': { w: 24, d: 12 },
  'dorm-mini-fridge': { w: 19, d: 12 },
  'dorm-shoe-rack': { w: 25, d: 12 },
  'dorm-printer': { w: 16, d: 6 },
  'dorm-beanbag': { w: 37, d: 12 },
  'dorm-3-tier-cart': { w: 14, d: 12 },
  // De-aliased items
  'dorm-stackable-drawers': { w: 24, d: 12 },
  'dorm-minifridge-compact': { w: 18, d: 12 },
  'dorm-minifridge-midsize': { w: 19, d: 12 },
  'dorm-rolling-cart': { w: 18, d: 12 },
};

// ---------------------------------------------------------------------------
// Legacy key -> current key pairs
// ---------------------------------------------------------------------------
// The 2026-07-14 de-alias pass split or renamed several item keys and left the
// old ones alive so already-persisted rooms keep resolving. Every behaviour Set
// in this package (and in items.js / scoring-helpers.js) must list both halves
// or neither — item behaviour lives in ~22 hand-maintained Sets, and listing only
// the legacy half is exactly the bug this table exists to prevent: the wizard
// and iOS offer the CURRENT key, so the item a user can actually pick was the
// one being excluded.
//
// `dorm-wastebasket` / `dorm-trash-can` is deliberately NOT a pair — those are
// genuine size variants (10x8x18 vs 12x9x20), both live and both offered.
//
// A pair is a shared LINEAGE, not an equivalence. Only the fridge row is a true
// rename with identical dimensions; the rest are different objects, and the
// differences are load-bearing:
//
//   dorm-fan          12x12x36  vs  dorm-tower-fan           6x6x30
//   dorm-air-purifier  7x10x27  vs  dorm-air-purifier-tower  8x8x22
//   dorm-laundry-bin  18x12x24  vs  dorm-laundry-hamper     14x14x24
//   dorm-3-tier-cart  14x18x31  vs  dorm-rolling-cart       18x14x33
//
// So symmetric MEMBERSHIP does not imply symmetric BEHAVIOUR, and reading it
// that way has already caused one bug: `dorm-fan` is 36" and never passed
// fitsUnderBed under a 32" captain, so its CAPTAIN_BACK_TIER listing was inert
// — and mirroring it onto the 30" tower fan, which does fit, put a 30" item in
// a row seated 26" back. canUseDeepRow is the height half of that judgement;
// this table only governs which keys appear in which Set.
//
// Enforced by __tests__/legacy-key-pair-symmetry.ci.test.js. Add a row here
// when a key is renamed and the guard covers it automatically.
const LEGACY_KEY_PAIRS = [
  ['dorm-mini-fridge', 'dorm-minifridge-midsize'],
  ['dorm-fan', 'dorm-tower-fan'],
  ['dorm-air-purifier', 'dorm-air-purifier-tower'],
  ['dorm-laundry-bin', 'dorm-laundry-hamper'],
  ['dorm-3-tier-cart', 'dorm-rolling-cart'],
];

const FACING_FLIP_ITEMS = new Set([
  'dorm-dresser',
  'dorm-wardrobe',
  'dorm-mini-fridge', // generic (orphan; kept for already-persisted objects)
  'dorm-minifridge-compact',
  'dorm-minifridge-midsize',
]);

const CAPTAIN_UNDERBED = new Set([
  'dorm-dresser',
  'dorm-bookshelf',
  // Shoes are the single most-recommended thing to keep under a bed, and a
  // shoe rack is 23" against a captain's 30" ceiling — it fits as-shipped, no
  // resize needed. Front tier below, since the whole point is reaching them.
  'dorm-shoe-rack',
  // Legacy + the current key the wizard and iOS offer; see LOFT_UNDERBED.
  'dorm-air-purifier',
  'dorm-air-purifier-tower',
  'dorm-laundry-bin',
  'dorm-wastebasket',
  'dorm-trash-can',
  'dorm-fan',
  'dorm-tower-fan',
  // De-aliased items
  'dorm-stick-vacuum',
  'dorm-handheld-vacuum',
  'dorm-storage-bin',
  'dorm-laundry-hamper',
]);

// A standard bed clears only 15" (13" once fitsUnderBed's 2" margin is taken),
// so this is deliberately tiny: purpose-built low-profile storage, nothing that
// needs to be opened or reached into. An under-bed storage bin is 7" tall and
// exists precisely for this gap — refusing it from under a bed was the bug.
// `low` beds stay empty on purpose (7" clear, 5" ceiling — nothing fits), and
// under-bed.js already branches its edge ordering on "captain / standard", so
// the placement path was built for this before the allowlist allowed it.
const STANDARD_UNDERBED = new Set([
  'dorm-storage-bin',
  'dorm-handheld-vacuum',
  // Listed, but the catalog shoe rack is 23" and still fails the 13" ceiling —
  // only a user-shortened one gets under a standard bed. That split is the
  // point: membership here answers "would this make sense?", while the height
  // gate stays in charge of "does it actually fit?".
  'dorm-shoe-rack',
]);

// Captain under-bed items a person has to stand and FACE: they get the exposed
// long edge or nothing (see placeUnderBed's `walkable` filter). Membership here
// is what makes that rule bind — an item in CAPTAIN_UNDERBED but in neither
// tier gets captainTier() === null, so isCaptainFront is false and it can be
// seated recessed against a wall edge, unreachable in a 32" cavity. The shoe
// rack was exactly that until 2026-08-24.
//
// Side effect worth knowing: front-tier items also inherit the 24"/16"
// drawer-clearance ladder, which exists for drawers a shoe rack does not have.
// It costs nothing (the ladder only tries for MORE room and falls back to the
// 8" floor), but don't read the membership as "this thing has drawers".
const CAPTAIN_FRONT_TIER = new Set([
  'dorm-dresser',
  'dorm-bookshelf',
  'dorm-shoe-rack',
]);
const CAPTAIN_BACK_TIER = new Set([
  // Legacy + the current key the wizard and iOS offer; see LOFT_UNDERBED.
  'dorm-air-purifier',
  'dorm-air-purifier-tower',
  'dorm-laundry-bin',
  'dorm-wastebasket',
  'dorm-trash-can',
  'dorm-fan',
  'dorm-tower-fan',
  'dorm-storage-bin',
  'dorm-laundry-hamper',
  'dorm-stick-vacuum',
  'dorm-handheld-vacuum',
]);

function captainTier(itemTypeKey) {
  if (CAPTAIN_FRONT_TIER.has(itemTypeKey)) return 'front';
  if (CAPTAIN_BACK_TIER.has(itemTypeKey)) return 'back';
  return null;
}

// ---------------------------------------------------------------------------
// Bed variant dimensions
// ---------------------------------------------------------------------------
// Beds are NOT selected like other items: the user picks a style (bed/bunk), a
// height (low/standard/captain/loft) and a room-wide size (twin/xl), and those
// three compose into the persisted iOS key `dorm-<style>-<height>-<size>`.
// So bed footprints live here rather than in ITEM_DIMS_INCHES — adding 14
// pseudo-items to the item catalog would leak them into every consumer that
// enumerates selectable items.
//
// Kept byte-identical to the iOS table in
// Housfully/Housfully/Models/DormItem.swift (`bedVariants`). Swift can't import
// this, so the two must be edited together.
const BED_WIDTH_INCHES = 42;
// XL twin is 4" longer than a standard twin — the only size axis dorms vary.
const BED_DEPTH_INCHES = { xl: 84, twin: 79 };
// Total height of the unit. A bunk is the full stack regardless of the deck
// height chosen for it, hence the flat 76.
const BED_HEIGHT_INCHES = { low: 21, standard: 36, captain: 43, loft: 76 };
const BUNK_HEIGHT_INCHES = 76;

const BED_SIZES = ['xl', 'twin'];
const BED_HEIGHT_KEYS = ['low', 'standard', 'captain', 'loft'];

/**
 * Footprint for a bed variant, in inches.
 * @param {string} height low | standard | captain | loft
 * @param {string} size   xl | twin
 * @param {boolean} isBunk
 */
function bedDimsInches(height, size, isBunk = false) {
  const d = BED_DEPTH_INCHES[size];
  const h = isBunk ? BUNK_HEIGHT_INCHES : BED_HEIGHT_INCHES[height];
  if (d == null || h == null) return null;
  return { w: BED_WIDTH_INCHES, d, h };
}

// Flat lookup keyed by the persisted iOS key, derived from the rules above so
// there is exactly one place to change a number. Consumed by the web-app's
// procedural gallery (defaultDimensions.js) as well as the optimizer.
const BED_DIMS_INCHES = {};
for (const size of BED_SIZES) {
  for (const height of BED_HEIGHT_KEYS) {
    BED_DIMS_INCHES[`dorm-bed-${height}-${size}`] = bedDimsInches(
      height,
      size,
      false
    );
    BED_DIMS_INCHES[`dorm-bunk-${height}-${size}`] = bedDimsInches(
      height,
      size,
      true
    );
  }
}

const LOFT_UNDERBED = new Set([
  'dorm-desk',
  'dorm-dresser',
  'dorm-wardrobe',
  'dorm-bookshelf',
  'dorm-mini-fridge',
  // Both de-aliased fridge variants belong here too. The de-alias pass added
  // the vacuums/bins below but missed these, so the ORPHAN generic key (kept
  // only for already-persisted objects) was the sole fridge that could tuck
  // under a loft while the two live ones could not. A loft is a canopy ~55"
  // up with open sides, not an enclosure, so it clears the 2-3" side/back
  // ventilation gap compact-fridge makers ask for — and a fridge under a
  // lofted bed is one of the most common dorm arrangements there is.
  // Deliberately NOT extended to CAPTAIN_UNDERBED: captain items are seated
  // flush, and nothing in this model represents a side/rear vent gap.
  'dorm-minifridge-compact',
  'dorm-minifridge-midsize',
  'dorm-shoe-rack',
  // A lounge nook under the loft is a real dorm arrangement, and a beanbag
  // is 37x37x27 — well inside the 42" loft width and the 53" ceiling. It is
  // the one FREESTANDING_ITEMS entry in this set; selectUnderLoftPersonalItems
  // already reads classified.freestanding, so no extra plumbing is needed.
  'dorm-beanbag',
  // Each of the next three pairs is one legacy key plus the CURRENT key the
  // wizard and iOS actually offer. The de-alias pass added the new keys to
  // ITEM_DIMS_INCHES but not here, so the item a user can really pick was the
  // one excluded from under a bed, while its unpickable legacy twin was let in.
  'dorm-air-purifier',
  'dorm-air-purifier-tower',
  'dorm-3-tier-cart',
  'dorm-rolling-cart',
  'dorm-laundry-bin',
  'dorm-wastebasket',
  'dorm-trash-can',
  'dorm-fan',
  'dorm-tower-fan',
  'dorm-floor-lamp',
  // De-aliased items
  'dorm-stick-vacuum',
  'dorm-handheld-vacuum',
  'dorm-storage-bin',
  'dorm-laundry-hamper',
]);

// Clear opening (floor -> underside of bed frame/deck), NOT total bed height,
// per variant. A per-bed `underBedClearanceInches` override (from the config)
// takes precedence over these defaults; see underBedClearanceInches().
// Confirmed against typical dorm bed specs (loft ~55" to the rail).
const UNDERBED_CLEARANCE_INCHES = {
  loft: 55,
  captain: 32,
  standard: 15,
  low: 7,
  bunk: 13,
};
const LOFT_EDGE_INSET_INCHES = 2;
// Extra perpendicular offset that pushes captain back-tier items into a hidden
// deeper row: a typical dresser depth (~24") + gap.
const CAPTAIN_BACK_ROW_INSET_INCHES = 26;
// Minimum headroom ABOVE an item for it to be allowed into that hidden row.
// Reaching something seated 26" back means getting a hand over it and gripping,
// which the item's own height eats into: a 30" item under a 32" bed leaves 2",
// and no hand fits. Expressed as a margin off the bed's OWN clearance rather
// than a fixed ceiling, because both halves vary — a user can type custom item
// dimensions AND a per-bed `underBedClearanceInches`, so a hardcoded number
// would be wrong for every non-default bed.
//
// Note this is deliberately STRICTER than fitsUnderBed. That gate answers "does
// it go in at all" and already ran; a deep-row rule set to the same threshold
// would be a no-op. 4" is the agreed margin: it keeps everything that lands
// there today except a 30" tower fan, which stays on the reachable edge.
const DEEP_ROW_HEADROOM_INCHES = 4;
// Outward approach-clearance depth used for captain FRONT-tier items
// (dresser/bookshelf) when seating them flush under a captain bed. Their full
// standalone clearance (e.g. a dresser's 24") projects far enough into the room
// to collide with the behind-bed desk and bump the item off the exposed long
// edge. A captain front-tier item shares the bed's own walk-up aisle, so a
// reduced drawer-pull gap is the right constraint here. Empirically the desk
// sits immediately at the exposed edge, leaving only ~10" of headroom before
// the approach zone collides; 8" clears every captain dresser in a full
// double-room sweep (0 bumped) with a small margin below that ~10" cliff.
const CAPTAIN_FRONT_TIER_CLEARANCE_INCHES = 8;
// Descending "clearance ladder" of upgrade rungs (biggest first) tried on the
// exposed long edge BEFORE falling back to the 8" floor above. Lets a captain
// dresser reserve real drawer room (24", then 16") when the exposed edge has
// the free depth, instead of jamming in at the first 8"-clear slot.
const CAPTAIN_FRONT_TIER_CLEARANCE_LADDER_INCHES = [24, 16];

// Clamp helper (mirrors kitchen-assembly/endpointSnap.js:127).
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Allowlist-only "does it make sense here?" predicate. Height is NOT considered
 * — fitsUnderBed() composes this with the height gate. Kept intact because the
 * two loft-footprint-sharing collision checks (wall.js / against-furniture.js)
 * and several call sites still rely on the pure semantic membership test.
 */
function canFitUnderBed(itemTypeKey, bedType) {
  if (bedType === 'bunk') return false;
  if (bedType === 'loft') return LOFT_UNDERBED.has(itemTypeKey);
  if (bedType === 'captain') return CAPTAIN_UNDERBED.has(itemTypeKey);
  if (bedType === 'standard') return STANDARD_UNDERBED.has(itemTypeKey);
  return false;
}

/**
 * Resolve the clear under-bed opening (inches) for a bed: a per-bed override
 * wins over the per-variant default; unknown variant → 0 (no clearance).
 */
function underBedClearanceInches(bed) {
  return (
    bed?.underBedClearanceInches ?? UNDERBED_CLEARANCE_INCHES[bed?.bedType] ?? 0
  );
}

/**
 * May this item be seated in a captain's hidden deep row?
 *
 * Back-tier membership says an item BELONGS out of sight; this says it can
 * still be retrieved from there. Both inputs are resolved per-instance —
 * `getItemDimsInches` honours a user-entered `dimsInches` and
 * `underBedClearanceInches` honours a per-bed override — so a student who
 * types their own sizes is gated on THEIR numbers, never the catalog's.
 *
 * Returns false for a non-captain bed or a non-back-tier item, so it is a
 * drop-in for the whole `allowDeepRow` condition at the call site.
 */
function canUseDeepRow(item, bed) {
  if (!bed || !isCaptainBed(bed.bedType)) return false;
  if (captainTier(item?.typeKey) !== 'back') return false;
  const dims = getItemDimsInches(item);
  if (!dims) return false;
  const clearance = underBedClearanceInches(bed);
  if (!clearance) return false;
  return dims.h <= clearance - DEEP_ROW_HEADROOM_INCHES;
}

function isLoftBed(bedType) {
  return bedType === 'loft';
}

function isCaptainBed(bedType) {
  return bedType === 'captain';
}

function isBunkBed(bedType) {
  return bedType === 'bunk';
}

function bedBelongsToOwner(bed, ownerId) {
  if (bed.ownerIds) return bed.ownerIds.includes(ownerId);
  return bed.ownerId === ownerId;
}

function getItemOwners(item) {
  if (item.ownerIds) return item.ownerIds;
  if (item.ownerId) return [item.ownerId];
  return [];
}

const BED_ACCESS_MIN_INCHES = 36;
// Loft ladders only need enough floor to stand and climb, not the full 36" a
// standard/bunk bed entry needs — 24" (2ft) is sufficient to reach the ladder.
const LOFT_LADDER_ACCESS_MIN_INCHES = 24;
const MIN_PATH_WIDTH_INCHES = 24;

const SCORING_WEIGHTS = {
  clearanceAccessibility: 20,
  bedPlacementQuality: 15,
  walkingSpace: 10,
  pathConnectivity: 8,
  ownerClustering: 10,
  storageGrouping: 6,
  wallPreference: 10,
  doorEntryZone: 5,
  windowUtilization: 4,
  outletProximity: 4,
  layoutSymmetry: 8,
  underBedSnug: 6,
};

const GOAL_WEIGHTS = {
  'study-focus': {
    clearanceAccessibility: 3,
    bedPlacementQuality: 8,
    walkingSpace: 8,
    pathConnectivity: 5,
    ownerClustering: 6,
    storageGrouping: 2,
    wallPreference: 7,
    doorEntryZone: 3,
    windowUtilization: 8,
    outletProximity: 6,
    layoutSymmetry: 2,
    deskBedSeparation: 16,
    deskLighting: 12,
    studyZone: 14,
    underBedSnug: 6,
  },
  'open-floor': {
    clearanceAccessibility: 8,
    bedPlacementQuality: 10,
    walkingSpace: 18,
    pathConnectivity: 5,
    ownerClustering: 5,
    storageGrouping: 2,
    wallPreference: 12,
    doorEntryZone: 4,
    windowUtilization: 2,
    outletProximity: 2,
    layoutSymmetry: 2,
    centralOpenness: 16,
    perimeterPacking: 8,
    underbedUtilization: 6,
    underBedSnug: 6,
  },
  workout: {
    clearanceAccessibility: 4,
    bedPlacementQuality: 8,
    walkingSpace: 12,
    pathConnectivity: 3,
    ownerClustering: 3,
    storageGrouping: 2,
    wallPreference: 10,
    doorEntryZone: 2,
    windowUtilization: 2,
    outletProximity: 2,
    layoutSymmetry: 2,
    centralOpenness: 12,
    perimeterPacking: 6,
    underbedUtilization: 8,
    yogaMatFit: 24,
    underBedSnug: 6,
  },
  'max-storage': {
    underbedUtilization: 20, // fill loft/captain under-bed slots (only scores with loft/captain)
    storageGrouping: 15, // dresser + wardrobe clustered per owner
    storageDensity: 14, // tight packing, no >6" gaps between storage
    perimeterPacking: 13, // push storage tight to walls
    wallPreference: 11, // items in wall/under-bed/surface placements
    clearanceAccessibility: 10, // storage stays reachable (open the drawers)
    ownerClustering: 8, // don't sprawl one person's storage across the room
    pathConnectivity: 5, // storage reachable from door
    doorEntryZone: 4, // don't block the entry with storage
    underBedSnug: 8, // tidy under-bed rows fit the max-storage goal
  },
  sleep: {
    bedDraft: 14, // beds away from door/window drafts
    lightSpill: 10, // desk lamp cones don't reach beds
    bedCozy: 10, // beds in corners, out of traffic paths
    noiseBuffer: 6, // solid furniture between bed and door
    bedPlacementQuality: 10,
    wallPreference: 8,
    deskBedSeparation: 7, // S12 rewards the sleep/study zone split directly
    clearanceAccessibility: 6,
    ownerClustering: 6,
    doorEntryZone: 6,
    walkingSpace: 5,
    pathConnectivity: 4,
    storageGrouping: 2,
    windowUtilization: 2,
    outletProximity: 2,
    layoutSymmetry: 2,
    underBedSnug: 6,
  },
  'natural-light': {
    deskWindowFacing: 16, // natural-light S13 variant — rewards desks facing the window head-on (view + light on surface)
    windowUtilization: 10, // S9 — desks near windows; also penalizes wardrobes blocking them
    clearanceAccessibility: 8,
    bedPlacementQuality: 8,
    wallPreference: 8,
    walkingSpace: 7,
    ownerClustering: 6,
    underBedSnug: 6,
    pathConnectivity: 5,
    doorEntryZone: 4,
    outletProximity: 3,
    storageGrouping: 2,
    layoutSymmetry: 2,
    // Deliberately omitted: studyZone (double-counts window proximity — that's
    // study-focus's job), deskBedSeparation, and lightSpill (which wants beds
    // dark, conflicting with maximizing daylight reach).
  },
  privacy: {
    // Phase 1 (scoring only). Tuned for the wall-hugging candidates the
    // generator produces today: real signals (separation, bed orientation,
    // bed placement) carry weight; the two Phase-2-dependent rules
    // (visualBarrier, changingPrivacy) are present but don't dominate.
    visualBarrier: 16, // P3 — tall furniture blocks cross-owner sightlines (dormant until Phase 2)
    ownerSeparation: 12, // P1 — each person's cluster on their own side
    changingPrivacy: 8, // P4 — wardrobe/dresser changing zones shielded (dormant until Phase 2)
    bedOrientation: 8, // P2 — beds turned away from each other
    bedPlacementQuality: 10, // corner beds ≈ per-owner zones (real signal today)
    ownerClustering: 6, // lowered — avoid fighting ownerSeparation
    layoutSymmetry: 4, // balanced two-person layouts
    wallPreference: 8,
    clearanceAccessibility: 6,
    doorEntryZone: 4,
    walkingSpace: 4,
    pathConnectivity: 4,
    storageGrouping: 4,
    underBedSnug: 6,
    windowUtilization: 2,
    outletProximity: 2,
  },
  gaming: {
    deskWallLength: 16, // S24 — desk on the longest clear wall run for a wide monitor setup (primary signal)
    deskTogether: 13, // S — shared battlestation: both desks flush-adjacent on one wall (co-op default)
    deskBedSeparation: 12, // S12 — keep the bed away from the desk (screen light + noise)
    outletProximity: 10, // S10 — desk within reach of an outlet for the rig
    lightSpill: 8, // S22 — bed shielded from late-night screen light
    bedPlacementQuality: 8,
    wallPreference: 8,
    clearanceAccessibility: 6,
    walkingSpace: 6,
    ownerClustering: 4, // lowered from 6 — clustering fights the shared battlestation
    underBedSnug: 6,
    pathConnectivity: 5,
    doorEntryZone: 4,
    windowUtilization: 2,
    storageGrouping: 2,
    layoutSymmetry: 2,
  },
};

// Short, human-readable blurb per optimization goal (keys match GOAL_WEIGHTS
// plus '' for the default Balanced weighting). Derived from each goal's boosted
// weights; rendered in the debug UI under the "Optimize for" dropdown.
const GOAL_DESCRIPTIONS = {
  '': 'Default weights — a balanced, well-rounded layout.',
  sleep:
    'Restful sleep: beds tucked away from door noise, window drafts & light, in cozier corners; desks kept clear of beds.',
  'study-focus':
    'Study setup: well-lit desks near windows, separated from beds, with a defined study zone.',
  'open-floor':
    'Maximizes open central floor: furniture packed to the perimeter and under beds.',
  workout:
    'Clears a central area for a workout/yoga mat; furniture pushed to walls and under beds.',
  'max-storage':
    'Fits the most storage: under-bed space, grouped & dense storage, packed perimeter.',
  'natural-light':
    'Maximizes daylight: desks placed to face nearby windows, with tall furniture kept clear of windows.',
  gaming:
    'Gaming / tech hub: desk on the longest wall for a wide monitor setup, close to an outlet, with the bed pushed away from late-night screen light and noise.',
  privacy:
    "Separation from your roommate: beds on opposite walls, each person's storage on their own side, and — where the room allows — two wardrobes stood mid-room as a divider, each opening toward its owner.",
};

// --- Extended item metadata ---

const SURFACE_HOSTS = {
  // Third instance of the same de-alias miss: the fridges a user can actually
  // pick are the compact/midsize keys, and neither was listed, so a microwave
  // could only ever perch on the ORPHAN legacy fridge. Both tops take it —
  // a microwave is 18x14 and the compact's top is 18x18.
  'dorm-microwave': new Set([
    'dorm-mini-fridge',
    'dorm-minifridge-compact',
    'dorm-minifridge-midsize',
    'dorm-dresser',
    'dorm-desk',
  ]),
  'dorm-desk-lamp': new Set(['dorm-desk']),
  'dorm-monitor': new Set(['dorm-desk']),
  'dorm-tv': new Set(['dorm-dresser', 'dorm-desk']),
  'dorm-printer': new Set(['dorm-desk', 'dorm-dresser', 'dorm-wardrobe']),
  // De-aliased items
  'dorm-desk-plant': new Set(['dorm-desk']),
  'dorm-desk-fan': new Set(['dorm-desk']),
  'dorm-surge-protector': new Set(['dorm-desk', 'dorm-dresser']),
};

const WALL_MOUNTED_ITEMS = new Set([
  'dorm-command-hooks',
  'dorm-mirror',
  'dorm-corkboard',
  'dorm-whiteboard',
  'dorm-floating-shelf',
  'dorm-poster',
  'dorm-tapestry',
  // Both light strings hang ON a wall. They used to sit in WALL_ADJACENT_ITEMS
  // — the set for things that stand on the FLOOR against a wall — so a
  // wizard-chosen one was seated at half its own height and, when it fell
  // through to the freestanding placer, parented to no wall at all
  // (#2315 / #2316). Style It has always hung them correctly; see
  // shared-lib/dorm-styling/plan/planLights.js.
  'dorm-fairy-lights',
  'dorm-fairy-lights-curtain',
]);

const DOOR_MOUNTED_ITEMS = new Set([
  'dorm-over-door-hooks',
  'dorm-over-door-mirror',
]);

const DESK_ZONE_ITEMS = new Set(['dorm-desk-chair']);

// Wall-mount specs: blocking rules differ per item
const WALL_MOUNT_SPECS_INCHES = {
  'dorm-mirror': {
    width: 20,
    // Centre height, in inches. 61" is where Style It hangs a mirror
    // (`WALL_ART_EYE_HEIGHT_M` = 1.55 m, with no per-type override), so a
    // chosen mirror lands where a themed one does.
    //
    // It is no longer `fullLength`. That flag made ANY against-wall furniture
    // block the span, which is right for a mirror standing on the floor — but
    // the asset is a 20x40 wall mirror hung at eye level, so it clears a desk
    // or dresser like the corkboard beside it. See #2317.
    mountHeight: 61,
    clearance: 24,
  },
  'dorm-corkboard': {
    width: 23,
    mountHeight: 54,
    blockingThreshold: 48,
    clearance: 0,
  },
  'dorm-whiteboard': {
    width: 24,
    mountHeight: 54,
    blockingThreshold: 48,
    clearance: 12,
  },
  'dorm-command-hooks': {
    width: 2,
    mountHeight: 60,
    blockingThreshold: 48,
    clearance: 0,
  },
  'dorm-floating-shelf': {
    width: 24,
    mountHeight: 54,
    blockingThreshold: 48,
    clearance: 0,
  },
  'dorm-poster': {
    width: 24,
    mountHeight: 60,
    blockingThreshold: 48,
    clearance: 0,
  },
  'dorm-tapestry': {
    width: 40,
    mountHeight: 60,
    blockingThreshold: 48,
    clearance: 0,
  },
  // String lights go up near the ceiling, the way Style It hangs them. The
  // 8"-tall run centred at 84" spans 80-88", so under the 96" ceiling a dorm
  // actually has it clears everything but a loft bed — which is also what keeps
  // its 8 ft width placeable: `blocked` only counts furniture taller than the
  // band's bottom edge.
  'dorm-fairy-lights': {
    width: 96,
    mountHeight: 84,
    blockingThreshold: 48,
    clearance: 0,
  },
  // The curtain hangs DOWN from near the ceiling: 60" tall centred at 60" spans
  // 30-90". Its band bottom is therefore low enough that a desk or dresser
  // genuinely does block it, which is correct — you cannot hang a 5 ft curtain
  // behind a wardrobe.
  'dorm-fairy-lights-curtain': {
    width: 60,
    mountHeight: 60,
    blockingThreshold: 48,
    clearance: 0,
  },
};

// Items that don't have clearance and can go wall or freestanding
const NO_CLEARANCE_SMALL_ITEMS = new Set([
  'dorm-floor-lamp',
  'dorm-trash-can',
  'dorm-fan',
  'dorm-wastebasket',
  'dorm-air-purifier',
  // De-aliased items
  'dorm-stick-vacuum',
  'dorm-handheld-vacuum',
  'dorm-floor-plant',
  'dorm-tower-fan',
  'dorm-air-purifier-tower',
  'dorm-air-purifier-compact',
]);

// Items that should be placed near the door rather than near the owner's bed
const DOOR_PROXIMITY_ITEMS = new Set(['dorm-shoe-rack', 'dorm-wastebasket']);

// Items that should be placed near the owner's desk
const DESK_PROXIMITY_ITEMS = new Set(['dorm-trash-can']);

// Items that can be rotated 90° to fit in tighter gaps
const ROTATABLE_ITEMS = new Set([
  'dorm-air-purifier',
  'dorm-wastebasket',
  'dorm-trash-can',
  'dorm-fan',
  'dorm-shoe-rack',
  'dorm-laundry-bin',
  // De-aliased items
  'dorm-stick-vacuum',
  'dorm-handheld-vacuum',
  'dorm-floor-plant',
  'dorm-tower-fan',
  'dorm-air-purifier-tower',
  'dorm-air-purifier-compact',
  'dorm-laundry-hamper',
]);

// Items that prefer corner/end-of-wall placement near a wardrobe. They may fall
// back to freestanding as an absolute last resort (Phase 7) if every wall/
// furniture-side spot fails, rather than going unplaced.
// These get an EARLY furniture-side attempt (before the owner-bed-wall try),
// since concealment against a furniture side is their whole point.
const CORNER_CONCEALED_ITEMS = new Set([
  'dorm-laundry-bin',
  'dorm-laundry-hamper',
]);

// Items eligible to tuck against the exposed side of the owner's furniture as a
// space-saving fallback. Superset of CORNER_CONCEALED_ITEMS: these get a LATE
// furniture-side attempt (after the normal wall cascade, before freestanding),
// since they have a wall/door preference first but shouldn't go unplaced when a
// furniture side is free. Excludes the mini-fridge (needs a wall + power +
// ventilation) and items that belong on a surface / wall / door.
const FURNITURE_SIDE_ITEMS = new Set([
  'dorm-laundry-bin',
  'dorm-laundry-hamper',
  'dorm-shoe-rack',
  'dorm-3-tier-cart',
  'dorm-rolling-cart',
  'dorm-stackable-drawers',
  'dorm-floor-plant',
  'dorm-storage-bin',
  'dorm-floor-lamp',
]);

/**
 * Get item dimensions in meters.
 * @param {string} typeKey
 * @returns {{w: number, d: number, h: number} | null}
 */
function getDimsMeters(typeKey) {
  const d = ITEM_DIMS_INCHES[typeKey];
  if (!d) return null;
  return { w: d.w * IN_TO_M, d: d.d * IN_TO_M, h: d.h * IN_TO_M };
}

/**
 * Resolve an item's actual footprint in INCHES. Prefers a per-instance
 * `dimsInches` override (iOS/backend custom dimensions) over the catalog
 * default. This is the single resolver; getItemDims is the meters view of it.
 *
 * Height gates that reason in inches (wall-decor bands, surface stacking)
 * should call this rather than indexing ITEM_DIMS_INCHES directly, so a
 * custom-sized item is judged at its real height.
 * @param {{typeKey: string, dimsInches?: {w: number, d: number, h: number}}} item
 * @returns {{w: number, d: number, h: number} | null}
 */
function getItemDimsInches(item) {
  return item?.dimsInches ?? ITEM_DIMS_INCHES[item?.typeKey] ?? null;
}

/**
 * Resolve an item's actual footprint in meters. Same return-shape as
 * getDimsMeters, so it's a drop-in at placement/collision/fit sites.
 * getDimsMeters itself is left untouched — a few callers legitimately want the
 * catalog default for a literal type (bed/dresser archetype heuristics).
 *
 * NOTE: the arithmetic is deliberately `<inches> * IN_TO_M`, matching
 * getDimsMeters exactly. fitsUnderBed's captain case lands on an exact float
 * equality (30" + 2" inset === 32" clearance === 0.8128 m), so reassociating
 * these multiplies would silently flip which items fit under a captain bed.
 * @param {{typeKey: string, dimsInches?: {w: number, d: number, h: number}}} item
 * @returns {{w: number, d: number, h: number} | null}
 */
function getItemDims(item) {
  const d = getItemDimsInches(item);
  if (!d) return null;
  return { w: d.w * IN_TO_M, d: d.d * IN_TO_M, h: d.h * IN_TO_M };
}

/**
 * Height-aware under-bed fit gate: true iff the item passes the semantic
 * allowlist AND its resolved height (plus the loft edge inset as a vertical
 * safety margin) clears the bed's resolved under-bed opening. Mirrors the 1D
 * height-vs-clearance check at under-loft.js (bookshelf-on-dresser stacking),
 * generalized to any item + any bed variant.
 * @param {{typeKey: string, dimsInches?: object}} item
 * @param {{bedType: string, underBedClearanceInches?: number}} bed
 */
function fitsUnderBed(item, bed) {
  if (!item || !bed) return false;
  if (!canFitUnderBed(item.typeKey, bed.bedType)) return false;
  const dims = getItemDims(item);
  if (!dims) return false;
  const clearanceM = underBedClearanceInches(bed) * IN_TO_M;
  const insetM = LOFT_EDGE_INSET_INCHES * IN_TO_M;
  return dims.h + insetM <= clearanceM;
}

/**
 * Get clearance zone dimensions in meters.
 * @param {string} typeKey
 * @returns {{w: number, d: number} | null}
 */
function getClearanceMeters(typeKey) {
  const c = CLEARANCE_ZONES_INCHES[typeKey];
  if (!c) return null;
  return { w: c.w * IN_TO_M, d: c.d * IN_TO_M };
}

function needsFacingFlip(typeKey) {
  return FACING_FLIP_ITEMS.has(typeKey);
}

module.exports = {
  MAX_CANDIDATES_PER_BED_CONFIG,
  IN_TO_M,
  WALL_HALF_THICKNESS,
  ITEM_DIMS_INCHES,
  BED_DIMS_INCHES,
  // The per-axis variant tables behind BED_DIMS_INCHES. Exported so the scan
  // classifier can name a scanned bed's variant by nearest catalog value,
  // rather than carrying a second set of height bands that could drift from
  // these.
  BED_HEIGHT_INCHES,
  BED_DEPTH_INCHES,
  bedDimsInches,
  DEFAULT_BUILTIN_INCHES,
  FIXED_OBSTACLE_CLEARANCE_M,
  CLEARANCE_ZONES_INCHES,
  FACING_FLIP_ITEMS,
  LEGACY_KEY_PAIRS,
  CAPTAIN_UNDERBED,
  CAPTAIN_FRONT_TIER,
  CAPTAIN_BACK_TIER,
  captainTier,
  LOFT_UNDERBED,
  STANDARD_UNDERBED,
  UNDERBED_CLEARANCE_INCHES,
  LOFT_EDGE_INSET_INCHES,
  CAPTAIN_BACK_ROW_INSET_INCHES,
  DEEP_ROW_HEADROOM_INCHES,
  canUseDeepRow,
  CAPTAIN_FRONT_TIER_CLEARANCE_INCHES,
  CAPTAIN_FRONT_TIER_CLEARANCE_LADDER_INCHES,
  BED_ACCESS_MIN_INCHES,
  LOFT_LADDER_ACCESS_MIN_INCHES,
  MIN_PATH_WIDTH_INCHES,
  SCORING_WEIGHTS,
  GOAL_WEIGHTS,
  GOAL_DESCRIPTIONS,
  SURFACE_HOSTS,
  WALL_MOUNTED_ITEMS,
  DOOR_MOUNTED_ITEMS,
  DESK_ZONE_ITEMS,
  WALL_MOUNT_SPECS_INCHES,
  NO_CLEARANCE_SMALL_ITEMS,
  DOOR_PROXIMITY_ITEMS,
  DESK_PROXIMITY_ITEMS,
  ROTATABLE_ITEMS,
  CORNER_CONCEALED_ITEMS,
  FURNITURE_SIDE_ITEMS,
  getDimsMeters,
  getItemDims,
  getItemDimsInches,
  getClearanceMeters,
  needsFacingFlip,
  canFitUnderBed,
  underBedClearanceInches,
  fitsUnderBed,
  clamp,
  isLoftBed,
  isCaptainBed,
  isBunkBed,
  bedBelongsToOwner,
  getItemOwners,
};
